/**
 * Модель сторінки «Нове» (редизайн v2): чиста логіка без DOM — групування списку
 * оновлень за твором, розбір назв розділів, діапазони номерів.
 *
 * Що вміє, чого не вміло плоске відображення «один рядок = один розділ»:
 *  - реліз на 40+ розділів стискається в один запис із діапазоном «Розділи 158–192»;
 *  - том — необов'язковий (ChapterItem.chapterNumber кодує його, див. chapter-volume.ts;
 *    якщо тому немає, показується лише «Розділ N»);
 *  - нумерація може починатись заново в кожному томі (ранобе: том 8 → 0.0–5.5,
 *    том 9 → 0.0–4.0) — тоді діапазон показується окремо для кожного тому;
 *  - номери бувають дробові («3.5», «0.0»), а в назві розділу — власний підзаголовок
 *    («Інтерлюдія. Де був Синій Птах…»).
 */
import type { UpdateItem } from '../api';
import { decodeChapterNumber, formatChapterUrlNumber } from '../chapter-volume';

export type ContentKind = 'novel' | 'manga' | 'anime';

export interface Chap {
  item: UpdateItem;
  vol?: number;
  /** Номер для показу: літерально як у назві ("4.0"), інакше з chapterNumber ("4"). */
  label: string;
  /** Власна назва розділу без префіксів "Том N" / "Розділ N" ("" — назви немає). */
  title: string;
  read: boolean;
  /** Ключ для сортування за зростанням. */
  sortKey: number;
  /** Інші копії цього ж розділу (дублі) — потрібні, щоб «Приховати» прибрало й їх. */
  dups: UpdateItem[];
}

export interface VolGroup {
  vol?: number;
  /** Від найновішого до найстарішого. */
  chapters: Chap[];
}

export interface Work {
  /** Унікальний ключ картки: твір + доба. */
  key: string;
  novelId: string;
  name: string;
  cover?: string;
  kind: ContentKind;
  /** Усі розділи за зростанням. */
  chapters: Chap[];
  /** Томи від найновішого; розділи без тому — в кінці. */
  volumes: VolGroup[];
  firstUnread?: Chap;
  hasUnread: boolean;
  hasTitles: boolean;
  /** Наскрізна нумерація між томами (158–192) чи починається заново (0.0–5.5, 0.0–4.0). */
  continuous: boolean;
  /** Усі записи картки разом із дублями (для приховування). */
  items: UpdateItem[];
  /** Найсвіжіший checkedAt серед розділів — за ним твір потрапляє в групу «Сьогодні/Вчора». */
  latestAt: string;
}

export interface SummaryLine {
  /** "Том 9", "Том 4–5" або порожньо. */
  vol: string;
  /** "Розділи 158–192", "Розділ 7". */
  range: string;
}

// «Том 5. Розділ 192 Назва», «Розділ 3.5 – Інтерлюдія», «Серія 8», «Chapter 12: …»
const NAME_RE =
  /^\s*(?:Том\s*(\d+(?:[.,]\d+)?)\s*[.,:;–—-]*\s*)?(?:Розділ|Глава|Chapter|Ch\.?|Серія|Епізод|Episode|Ep\.?)\s*(\d+(?:[.,]\d+)?)\s*[.,:;–—-]*\s*(.*)$/i;

export interface ParsedName {
  vol?: number;
  numStr?: string;
  title: string;
}

export function parseChapterName(name: string): ParsedName {
  const m = NAME_RE.exec(name || '');
  if (!m) return { title: (name || '').trim() };
  return {
    vol: m[1] ? Number(m[1].replace(',', '.')) : undefined,
    numStr: m[2].replace(',', '.'),
    title: (m[3] || '').trim(),
  };
}

export function makeChap(item: UpdateItem, index: number): Chap {
  const parsed = parseChapterName(item.chapterName);
  let vol: number | undefined;
  let inVol: number | undefined;
  if (item.chapterNumber !== undefined) {
    const d = decodeChapterNumber(item.chapterNumber);
    vol = d.volume;
    inVol = d.chapterInVolume;
  }
  if (vol === undefined) vol = parsed.vol;
  const fromName = parsed.numStr !== undefined ? parseFloat(parsed.numStr) : undefined;
  const num = inVol ?? fromName;
  const label =
    parsed.numStr !== undefined && fromName === num
      ? parsed.numStr
      : num !== undefined
        ? formatChapterUrlNumber(num)
        : '';
  // Якщо назва не схожа на "Розділ N …", вважаємо її власною назвою — але лише коли
  // це не просто номер.
  let title = parsed.title;
  if (/^\d+(?:[.,]\d+)?$/.test(title)) title = '';
  return {
    item,
    vol,
    label,
    title,
    read: item.read,
    dups: [],
    // index лише розв'язує нічиї для розділів без числового номера
    sortKey: (vol ?? 0) * 1e6 + (num !== undefined && !Number.isNaN(num) ? num : index * 1e-3),
  };
}

function firstUnreadOf(chs: Chap[]): Chap | undefined {
  return chs.find((c) => !c.read);
}

/** Початок локальної доби для ISO-часу. */
export function dayStartOf(iso: string): number {
  const d = new Date(iso);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Ключ «це той самий розділ»: твір + том + номер (або назва, якщо номера немає). */
function dupKey(c: Chap): string {
  return `${c.item.novelId}|${c.vol ?? ''}|${c.label || c.title || c.item.chapterName}`;
}

/**
 * Групує плоский список оновлень у картки «твір × день».
 *  - Один і той самий розділ/серія, що прийшов кілька разів (різні chapterPath, повторне оголошення),
 *    лишається ОДИН раз — у день, коли його вперше побачили; прочитаним він вважається, якщо
 *    прочитана хоч одна копія.
 *  - Картка об'єднує розділи лише в межах однієї доби: що прийшло вчора, у «Сьогодні» не потрапляє.
 */
export function buildWorks(
  items: UpdateItem[],
  kindOf: (pluginId: string) => ContentKind,
  opts: { hideRead?: boolean } = {},
): Work[] {
  const all = items.map(makeChap).sort((a, b) => a.item.checkedAt.localeCompare(b.item.checkedAt));
  const seen = new Map<string, Chap>();
  for (const c of all) {
    const k = dupKey(c);
    const first = seen.get(k);
    if (!first) seen.set(k, c);
    else {
      first.dups.push(c.item);
      if (c.read) first.read = true;
    }
  }
  const unique = [...seen.values()].filter((c) => !(opts.hideRead && c.read));

  const buckets = new Map<string, Chap[]>();
  for (const c of unique) {
    const key = `${c.item.novelId}|${dayStartOf(c.item.checkedAt)}`;
    const list = buckets.get(key);
    if (list) list.push(c);
    else buckets.set(key, [c]);
  }

  const works: Work[] = [];
  for (const [key, list] of buckets) {
    const chapters = list.sort((a, b) => a.sortKey - b.sortKey);
    const groups = new Map<number | undefined, Chap[]>();
    chapters.forEach((c) => {
      const g = groups.get(c.vol);
      if (g) g.push(c);
      else groups.set(c.vol, [c]);
    });
    const volumes: VolGroup[] = [...groups.entries()]
      .sort((a, b) => (a[0] === undefined ? 1 : b[0] === undefined ? -1 : b[0] - a[0]))
      .map(([vol, chs]) => ({ vol, chapters: [...chs].reverse() }));

    const first = chapters[0].item;
    const latestAt = chapters.reduce((m, c) => (c.item.checkedAt > m ? c.item.checkedAt : m), first.checkedAt);
    works.push({
      key,
      novelId: first.novelId,
      name: first.novelName,
      cover: first.cover,
      kind: kindOf(first.pluginId),
      chapters,
      items: chapters.flatMap((c) => [c.item, ...c.dups]),
      volumes,
      firstUnread: firstUnreadOf(chapters),
      hasUnread: chapters.some((c) => !c.read),
      hasTitles: chapters.some((c) => c.title),
      continuous: isContinuous(volumes),
      latestAt,
    });
  }
  return works;
}

/** Розділ, на який веде картка: перший непрочитаний (читаємо по порядку); якщо все прочитано — найновіший. */
export function targetChapter(w: Work): Chap {
  return w.firstUnread ?? w.chapters[w.chapters.length - 1];
}

function numOf(c: Chap): number {
  const n = parseFloat(c.label);
  return Number.isNaN(n) ? 0 : n;
}

/** true, якщо номери розділів зростають між томами без повернення до 0/1 (напр. том 4: 153–172, том 5: 173–192). */
export function isContinuous(volumes: VolGroup[]): boolean {
  const vs = volumes.filter((v) => v.vol !== undefined).slice().reverse(); // від старшого тому до новішого
  if (vs.length < 2) return true;
  let prevHi = -Infinity;
  for (const v of vs) {
    const nums = v.chapters.map(numOf);
    const lo = Math.min(...nums);
    const hi = Math.max(...nums);
    if (lo <= prevHi) return false;
    prevHi = hi;
  }
  return true;
}

function rangeOf(chs: Chap[]): string {
  // chs — за зростанням
  const a = chs[0].label;
  const b = chs[chs.length - 1].label;
  return a === b ? a : `${a}–${b}`;
}

export function words(kind: ContentKind): { one: string; many: string; read: string } {
  return kind === 'anime'
    ? { one: 'Серія', many: 'Серії', read: 'Дивитись з' }
    : { one: 'Розділ', many: 'Розділи', read: 'Читати з' };
}

/** Рядки під назвою твору. Рахуються за НЕПРОЧИТАНИМИ розділами (якщо всі прочитані — за всіма). */
export function summaryLines(w: Work): SummaryLine[] {
  const pool = w.chapters.some((c) => !c.read) ? w.chapters.filter((c) => !c.read) : w.chapters;
  const wd = words(w.kind);
  const word = (chs: Chap[]) => (chs.length === 1 ? wd.one : wd.many);
  const vols = [...new Set(pool.map((c) => c.vol))];
  const defined = vols.filter((v): v is number => v !== undefined);

  if (w.continuous || defined.length < 2) {
    const volTxt = !defined.length
      ? ''
      : defined.length === 1
        ? `Том ${defined[0]}`
        : `Том ${Math.min(...defined)}–${Math.max(...defined)}`;
    return [{ vol: volTxt, range: `${word(pool)} ${rangeOf(pool)}` }];
  }

  // нумерація стартує заново в кожному томі → окремий діапазон на том, від найновішого
  const sortedVols = [...defined].sort((a, b) => b - a);
  const lines: SummaryLine[] = sortedVols.slice(0, 2).map((v) => {
    const chs = pool.filter((c) => c.vol === v);
    return { vol: `Том ${v}`, range: `${word(chs)} ${rangeOf(chs)}` };
  });
  if (sortedVols.length > 2) lines.push({ vol: '', range: `+ ще ${sortedVols.length - 2} ${sortedVols.length - 2 === 1 ? 'том' : 'томи'}` });
  return lines;
}

/** Підпис кнопки «Читати з …» (вказує том, лише якщо нумерація повторюється між томами). */
export function ctaText(w: Work): string {
  const c = w.firstUnread;
  if (!c) return '';
  const wd = words(w.kind);
  const needVol = !w.continuous && c.vol !== undefined;
  return `${wd.read} ${needVol ? `Том ${c.vol} · ` : ''}${c.label}`;
}

/** Підпис для оновлення: чи з'явилось щось нове відносно попереднього знімка. */
export function unreadSignature(items: UpdateItem[]): string {
  let unread = 0;
  let latest = '';
  for (const u of items) {
    if (!u.read) unread++;
    if (u.checkedAt > latest) latest = u.checkedAt;
  }
  return `${items.length}|${unread}|${latest}`;
}

export function unreadCount(items: UpdateItem[]): number {
  return items.filter((u) => !u.read).length;
}
